<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import { transformYearlyFortuneText } from '@/composables/splitSentence';
import MonthlyChart from './MonthlyChart.vue';
import emojiEnergy0 from '@/assets/images_manse/energy/emoji_energy0.png';
import emojiEnergy1 from '@/assets/images_manse/energy/emoji_energy1.png';
import emojiEnergy2 from '@/assets/images_manse/energy/emoji_energy2.png';
import emojiEnergy3 from '@/assets/images_manse/energy/emoji_energy3.png';
import emojiEnergy3_5 from '@/assets/images_manse/energy/emoji_energy3_5.png';
import emojiEnergy4 from '@/assets/images_manse/energy/emoji_energy4.png';
import emojiEnergy4_5 from '@/assets/images_manse/energy/emoji_energy4_5.png';
import emojiEnergy5 from '@/assets/images_manse/energy/emoji_energy5.png';

const { getFiveClass, getGanjiColorFive, getHanjaTxt2 } = fiveMixin();

const props = defineProps({
  userName: { type: String, default: '' },
  isMobile: { type: Boolean, default: false },
  data: { type: Object, default: () => ({}) },
});

// 데이터 접근
const 전체요약 = computed(() => props.data?.전체요약 || {});
const 월별현황 = computed(() => 전체요약.value?.월별현황 || {});
const 월별전략 = computed(() => 전체요약.value?.월별전략 || {});

// 월 키 배열
const monthKeys = [
  '1월',
  '2월',
  '3월',
  '4월',
  '5월',
  '6월',
  '7월',
  '8월',
  '9월',
  '10월',
  '11월',
  '12월',
];

// 등급별 색상 (피그마 디자인 기준)
const gradeColors = {
  '최고의 달': { color: '#c62828', bg: 'rgba(198,40,40,0.05)' },
  '활기찬 달': { color: '#e65100', bg: 'rgba(230,81,0,0.05)' },
  '안정의 달': { color: '#9f37ca', bg: 'rgba(159,55,202,0.05)' },
  '무난한 달': { color: '#1976d2', bg: 'rgba(25,118,210,0.05)' },
  '점검의 달': { color: '#548235', bg: 'rgba(84,130,53,0.05)' },
  '충전의 달': { color: '#8bc34a', bg: 'rgba(139,195,74,0.05)' },
};

// 등급에서 색상 가져오기 (fallback: 회색)
const getGradeColor = (grade) => {
  return gradeColors[grade] || { color: '#999', bg: 'rgba(153,153,153,0.05)' };
};

// 점수별 이모지 이미지 (5=최고, 4.5=충만, 4=행복, 3.5=설렘, 3=기대, 2.5=무난, 2=준비, 1=휴식)
const getScoreEmoji = (score) => {
  if (score > 4.5) return emojiEnergy5;
  if (score >= 4.0) return emojiEnergy4_5;
  if (score >= 3.5) return emojiEnergy4;
  if (score >= 3.0) return emojiEnergy3_5;
  if (score >= 2.5) return emojiEnergy3;
  if (score >= 2.0) return emojiEnergy2;
  if (score >= 1.0) return emojiEnergy1;
  return emojiEnergy0;
};

// 월별 데이터 (현황 + 전략 합침)
const 월별데이터 = computed(() => {
  return monthKeys.map((key) => {
    const 현황 = 월별현황.value?.[key] || {};
    const 전략 = 월별전략.value?.[key] || {};
    return {
      월: key,
      간지: 전략?.간지 || '',
      점수: 현황?.점수 || 0,
      확률: 현황?.확률 || 0,
      등급: 전략?.등급 || '',
      가이드: 전략?.가이드 || '',
    };
  });
});

// 왼쪽 (1~4월), 오른쪽 (5~12월)
const leftMonths = computed(() => 월별데이터.value.slice(0, 4));
const rightMonths = computed(() => 월별데이터.value.slice(4));

// 차트 데이터 (12개월 확률)
const chartData = computed(() => {
  return 월별데이터.value.map((item) => item.확률);
});
</script>

<template>
  <VRow class="full-height over-hidden">
    <!-- 왼쪽 컬럼: 차트 + 1~4월 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="monthly-wrapper" :class="{ mobile: isMobile }">
          <!-- 월별 에너지 흐름 -->
          <section class="monthly-section">
            <div class="monthly-section__title">
              <span>🔋</span>
              <span>2026년 월별 에너지 흐름</span>
            </div>
            <p class="monthly-section__desc">12개월의 흐름을 한눈에 살펴봅니다.</p>

            <MonthlyChart :data="chartData" :isMobile="isMobile" />
          </section>

          <div class="monthly-divider"></div>

          <!-- 1~4월 카드 -->
          <section class="monthly-section">
            <div class="monthly-section__title">
              <span>🚀</span>
              <span>월별 에너지 활용 가이드</span>
            </div>

            <div class="month-cards" :class="{ mobile: isMobile }">
              <div
                v-for="(item, index) in leftMonths"
                :key="index"
                class="month-card"
                :class="{ mobile: isMobile }"
                :style="{ backgroundColor: getGradeColor(item.등급).bg }"
              >
                <div class="month-card__row">
                  <div class="month-card__badge-wrap">
                    <span
                      class="month-card__badge"
                      :style="{ backgroundColor: getGradeColor(item.등급).color }"
                    >
                      {{ item.월 }}
                    </span>
                    <span v-if="item.간지" class="month-card__ganji">
                      <span
                        v-for="(char, ci) in item.간지.split('')"
                        :key="ci"
                        class="ganji-txt"
                        :class="getFiveClass(getGanjiColorFive(char))"
                        >{{ getHanjaTxt2(char) || char }}</span
                      >
                      <span class="month-card__ganji-suffix">月</span>
                    </span>
                  </div>
                  <div class="month-card__info">
                    <span class="month-card__grade" :style="{ color: getGradeColor(item.등급).color }">
                      {{ item.등급 }}
                    </span>
                    <p
                      class="month-card__subtitle"
                      v-html="transformYearlyFortuneText(item?.가이드)"
                    ></p>
                  </div>
                  <img
                    class="month-card__emoji"
                    :src="getScoreEmoji(item.점수)"
                    :alt="item.등급"
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </VCard>
    </VCol>

    <!-- 오른쪽 컬럼: 5~12월 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="monthly-wrapper right-col" :class="{ mobile: isMobile }">
          <div class="month-cards" :class="{ mobile: isMobile }">
            <div
              v-for="(item, index) in rightMonths"
              :key="index"
              class="month-card"
              :class="{ mobile: isMobile }"
              :style="{ backgroundColor: getGradeColor(item.등급).bg }"
            >
              <div class="month-card__row">
                <div class="month-card__badge-wrap">
                  <span
                    class="month-card__badge"
                    :style="{ backgroundColor: getGradeColor(item.등급).color }"
                  >
                    {{ item.월 }}
                  </span>
                  <span v-if="item.간지" class="month-card__ganji">
                    <span
                      v-for="(char, ci) in item.간지.split('')"
                      :key="ci"
                      class="ganji-txt"
                      :class="getFiveClass(getGanjiColorFive(char))"
                      >{{ getHanjaTxt2(char) || char }}</span
                    >
                    <span class="month-card__ganji-suffix">月</span>
                  </span>
                </div>
                <div class="month-card__info">
                  <span class="month-card__grade" :style="{ color: getGradeColor(item.등급).color }">
                    {{ item.등급 }}
                  </span>
                  <p class="month-card__subtitle">{{ item.가이드 }}</p>
                </div>
                <img
                  class="month-card__emoji"
                  :src="getScoreEmoji(item.점수)"
                  :alt="item.등급"
                />
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
.monthly-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  padding-bottom: 130px;
  gap: 24px;

  &.mobile {
    padding: 24px 16px;
    padding-bottom: 24px;
  }

  &.right-col {
    justify-content: flex-start;
  }
}

.monthly-section {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__title {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  &__desc {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    margin: 0;
  }
}

.monthly-divider {
  width: 100%;
  height: 1px;
  background: #ebebeb;
}

.month-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;

  &.mobile {
    gap: 10px;
    margin-top: 0;
    margin-left: -16px;
    margin-right: -16px;
  }
}

.month-card {
  border-radius: 12px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.mobile {
    border-radius: 0;
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__info {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 5px;
    min-width: 0;
  }

  &__badge-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    width: 42px;
    flex-shrink: 0;
  }

  &__ganji {
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    line-height: 24px;
    text-align: center;
  }

  &__ganji-suffix {
    color: #575757;
  }

  &__badge {
    width: 100%;
    padding: 2px 0px;
    border-radius: 22px;
    color: #fff;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.4;
    text-align: center;
  }

  &__grade {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  &__emoji {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    object-fit: contain;
  }

  &__subtitle {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    margin: 0;
  }
}
</style>
